/**
 * @fileoverview Tracing configuration management
 * @module config
 */

/**
 * Default configuration values
 * @constant {Object}
 */
const DEFAULT_CONFIG = {
  enabled: true,
  samplingRatio: 1.0,
  includeDebugHeaders: true,
};

/**
 * Global tracing configuration
 * Can be modified to control browser-side tracing behavior
 * @type {Object}
 */
export const TracingConfig = {
  /**
   * Whether tracing is enabled (default: true)
   * When false, trace headers are still generated but sampled=false
   */
  enabled: DEFAULT_CONFIG.enabled,

  /**
   * Sampling ratio (0.0-1.0, default: 1.0)
   * 1.0 = 100% of traces are sampled
   * 0.1 = 10% of traces are sampled
   */
  samplingRatio: DEFAULT_CONFIG.samplingRatio,

  /**
   * Whether to include debug headers (x-trace-id, x-span-id)
   * Set to false in production if you want minimal headers
   */
  includeDebugHeaders: DEFAULT_CONFIG.includeDebugHeaders,
};

/**
 * Configure tracing behavior
 * @param {Object} config - Configuration options
 * @param {boolean} [config.enabled] - Enable/disable tracing
 * @param {number} [config.samplingRatio] - Sampling ratio (0.0-1.0)
 * @param {boolean} [config.includeDebugHeaders] - Include debug headers
 */
export function configureTracing(config = {}) {
  if (config.enabled !== undefined) {
    TracingConfig.enabled = config.enabled;
  }
  if (config.samplingRatio !== undefined) {
    TracingConfig.samplingRatio = Math.max(0, Math.min(1, config.samplingRatio));
  }
  if (config.includeDebugHeaders !== undefined) {
    TracingConfig.includeDebugHeaders = config.includeDebugHeaders;
  }
}

/**
 * Determines if a trace should be sampled based on configuration
 * @returns {boolean} True if trace should be sampled
 */
/**
 * Configure tracing from a Vite-style env bag — the ONE bootstrap seam every web app calls
 * (BROWSER-TELEMETRY-001 P1). Values are strings (Vite env), absence means the package default,
 * and the call RESETS to the defaults first so a stale value can never leak between boots.
 *
 * - `VITE_TRACING_ENABLED` — disabled only on the literal `"false"` (case-insensitive).
 * - `VITE_TRACING_SAMPLING_RATIO` — a number in [0, 1]; clamped; a non-number keeps the default.
 * - `VITE_TRACING_DEBUG_HEADERS` — the `x-trace-id` / `x-span-id` mirrors, off only on `"false"`.
 *
 * @param {Object<string, string>} [env] - The three keys above, each a NAMED read (`{ VITE_TRACING_ENABLED:
 *   import.meta.env.VITE_TRACING_ENABLED, … }`). NEVER `import.meta.env` itself: a whole-object read makes Vite inline EVERY
 *   `VITE_*` key the app's spec sets into the public bundle (SECURITY-HARDENING-GEMS § A; `viteEnvNamedReadsOnly` guards it).
 * @returns {{enabled: boolean, samplingRatio: number, includeDebugHeaders: boolean}} the applied config
 */
export function configureTracingFromEnv(env = {}) {
  resetConfig();
  const ratio = Number.parseFloat(env.VITE_TRACING_SAMPLING_RATIO);
  configureTracing({
    enabled: !isLiteralFalse(env.VITE_TRACING_ENABLED),
    samplingRatio: Number.isFinite(ratio) ? ratio : undefined,
    includeDebugHeaders: !isLiteralFalse(env.VITE_TRACING_DEBUG_HEADERS),
  });
  return { ...TracingConfig };
}

function isLiteralFalse(value) {
  return typeof value === 'string' && value.toLowerCase() === 'false';
}

export function shouldSample() {
  if (!TracingConfig.enabled) {
    return false;
  }
  if (TracingConfig.samplingRatio >= 1.0) {
    return true;
  }
  if (TracingConfig.samplingRatio <= 0) {
    return false;
  }
  return Math.random() < TracingConfig.samplingRatio;
}

/**
 * Reset configuration to defaults (for testing)
 */
export function resetConfig() {
  TracingConfig.enabled = DEFAULT_CONFIG.enabled;
  TracingConfig.samplingRatio = DEFAULT_CONFIG.samplingRatio;
  TracingConfig.includeDebugHeaders = DEFAULT_CONFIG.includeDebugHeaders;
}
